import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Config, EnumValue, registerBrick, Transaction} from 'olympe';
import {ReactBrick, useProperty} from '@olympeio/core';
import {map} from 'rxjs/operators';
import {Alert, FormControl, Grid, MenuItem, Select, Snackbar, TextField, Tooltip} from '@mui/material';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    olympeDBCreateLambda,
    downloadRecordsLambda,
    encryptPasswordLambda,
    getVendorNameLambda,
    sendEmailLambda,
    olympeDBUpdateLambda, olympeDBDeleteLambda, olympeDBReadLambda,
} from '../utils/functionSignatures';
import {ColumnsDataTypeController} from '../controllers/ColumnsDataTypeController';
import {
    buildIdAndAddToList,
    parseError,
    prepareDataForPostReq,
} from '../utils/adminApp';
import {LicenseInfo} from '@mui/x-license-pro';
import {
    DataGridPro,
    getGridStringOperators,
    GridActionsCellItem,
    GridLinkOperator,
    GridRowModes,
    GridToolbarContainer,
} from '@mui/x-data-grid-pro';
import UploadRecordsPopup from './dialogs/UploadRecordsPopup.jsx';
import AdminChangeLog from '../models/adminApp/AdminChangeLog';
import UserController from '../controllers/UserController';
import {combineLatest} from 'rxjs';
import {getMailContentAdminAppChange} from '../utils/email';
import {format} from 'date-fns';
import {ADMIN_APP_TABLE_COLUMNS, ADMIN_APP_TABLE_NAMES, LAMBDA_NAMES} from '../utils/constants';
import {ErrorPopup} from './pr_editor/dialogs/ErrorPopup.jsx';
import {VendorCodeEditCell} from './datagrid/components/VendorCodeEditCell.jsx';
import {ProtocolChip} from './datagrid/components/ProtocolChip.jsx';

const PasswordEditCell = (props) => {
    const {id, field, api} = props;
    const [value, setValue] = useState('');

    const handleChange = (event) => {
        const newValue = event.target.value;
        setValue(newValue);
        api.setEditCellValue({id, field, value: newValue});
    };

    return (
        <TextField
            type="text"
            value={value}
            onChange={handleChange}
            fullWidth
        />
    );
};

/**
 * Brick that display a list of tables
 */
export default class Datagrid extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Hidden', false),
            $.observe('list').pipe(map((options) => options.getValues().map((v) => {
                return {
                    value: v.get(EnumValue.valueProp),
                    name: v.get(EnumValue.nameProp),
                };
            }))),
        ]);
    }

    /**
     * @override
     */
    init($) {
        super.init($);

        // License key for DataGrid Pro
        LicenseInfo.setLicenseKey(Config.getParameter('datagrid_license'));
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [hidden, options] = props.values;
            const [rows, setRows] = useState([]);
            const [rowModesModel, setRowModesModel] = useState({});
            const [columnsString, setColumnsString] = useState('');
            const [columnsDisplayName, setColumnsDisplayName] = useState(new Map());
            const [primaryKeysColumn, setPrimaryKeysColumn] = useState('');
            const selectedTable = useProperty($, 'selectedTable');
            const companyCodes = useProperty($, 'companyCodes', true);
            const selectedTableName = useRef('');
            const [filterState, setFilterState] = useState([]);
            const [sortingState, setSortingState] = useState({
                field: 'idx',
                sort: 'asc',
            });
            const [pageState, setPageState] = useState({
                isLoading: false,
                columns: [],
                total: 0,
                page: 0,
                pageSize: 10,
            });

            const [adminUserList, setAdminUserList] = useState([]);
            const sendEmail = $.get(LAMBDA_NAMES.SEND_EMAIL);

            const [showUploadRecords, setShowUploadRecords] = useState(false);
            const [snackbar, setSnackbar] = useState(null);
            const [chipFilters, setChipFilters] = useState([]);
            const rowsDisabled = selectedTable === 'admin_app_user_list_mapping' ? false : companyCodes?.length === 0;

            const [showErrorPopup, setShowErrorPopup] = useState(false);
            const [errorMessages, setErrorMessages] = useState([]);

            const checkIfSortFieldExists = (columnsString, sortField) => {
                if (columnsString.includes(sortField.field)) {
                    return sortField;
                }
                return {field: '', sort: ''};
            };

            // Update chipFilters from companyCodes and reset page if changed
            useEffect(() => {
                if (companyCodes) {
                    const newChipFilters = companyCodes;
                    setChipFilters((prev) => {
                        const hasChanged = !Array.isArray(chipFilters) || prev.length !== newChipFilters.length || !prev.every((filter, index) => filter === newChipFilters[index]);
                        if (hasChanged) {
                            setPageState((prevState) => ({...prevState, page: 0}));
                            return newChipFilters;
                        }
                        return prev;
                    });
                }
            }, [companyCodes]);

            // Combine external company_code filters with other filters in filterState
            useEffect(() => {
                if (chipFilters) {
                    const otherFilters = filterState.filter((filter) => filter.filterColumn !== 'company_code');
                    const companyCodeFilter = chipFilters.length > 0 ? [{
                        filterColumn: 'company_code',
                        filterValue: chipFilters,
                    }] : [];
                    const newFilterState = [...otherFilters, ...companyCodeFilter];

                    setFilterState((prev) => {
                        const hasChanged = prev.length !== newFilterState.length ||
                            !prev.every((filter, index) =>
                                filter.filterColumn === newFilterState[index]?.filterColumn &&
                                JSON.stringify(filter.filterValue) === JSON.stringify(newFilterState[index]?.filterValue),
                            );
                        if (hasChanged) {
                            return newFilterState;
                        }
                        return prev;
                    });
                }
            }, [chipFilters]);


            const handleCloseSnackbar = () => setSnackbar(null);

            const handleProcessSuccess = useCallback((message) => {
                setSnackbar({children: message, severity: 'success'});
            }, []);

            const handleProcessError = useCallback((message) => {
                setSnackbar({children: message, severity: 'error'});
            }, []);

            const handleChangeSelectedTable = (event) => {
                $.set('selectedTable', event.target.value);
                setPageState((prevState) => ({...prevState, page: 0}));
                setSortingState((prev) => ({...prev, field: 'idx', sort: 'asc'}));
                setColumnsString('');
                setColumnsDisplayName(new Map);
                // Remove filter related to table when changing table
                const companyCodeFilter = chipFilters.length > 0 ? [{filterColumn: 'company_code', filterValue: chipFilters}] : [];
                setFilterState([...companyCodeFilter]);
            };

            // Fetch table columns and metadata when selectedTable or chipFilters change
            useEffect(async () => {
                if (selectedTable) {
                    selectedTableName.current = options.find((item) => item.value === selectedTable)?.name;
                    const columnsData = await ColumnsDataTypeController.getFilteredColumns(selectedTable);
                    const fromIndex = columnsData.findIndex((col) => col.column === 'idx');
                    const idElement = columnsData[fromIndex];
                    if (idElement) {
                        columnsData.splice(fromIndex, 1);
                        columnsData.splice(0, 0, idElement);
                    }
                    const primaryKeysColumn = await ColumnsDataTypeController.getPrimaryKeysColumn($, selectedTable);
                    setPrimaryKeysColumn(primaryKeysColumn.split(';'));
                    const columns = columnsData.map((item) => item.column);
                    setColumnsString(columns.join(', '));
                    setColumnsDisplayName(new Map(columnsData.map((cd) => [cd.column, cd.columnDisplayName])));
                    setPageState((prevState) => ({...prevState, columns: columnsData}));
                }
            }, [selectedTable]);

            const fetchAdminUserListData = useCallback(() => {
                olympeDBReadLambda($, $.get(LAMBDA_NAMES.OLYMPE_DB_READ), 'admin_app_user_list_mapping', filterState, 10000, 0)
                    .then((result) => {
                        const parsedResult = JSON.parse(result[0]);
                        setAdminUserList(parsedResult);
                    })
                    .catch((error) => {
                        console.error(`Cannot read data from DB. ${error}`);
                        setAdminUserList([]);
                    });
            }, []);

            // Fetch admin user list data on component mount
            useEffect(() => {
                fetchAdminUserListData();
            }, []);

            // Recreate fetchMainData as useCallback for manual and automatic calls
            const fetchMainData = useCallback((bypassEditMode) => {
                const editMode = rows.some((row) => row.isNew);

                if (columnsString && selectedTable && primaryKeysColumn && filterState && pageState.page >= 0 && pageState.pageSize > 0 && (bypassEditMode || !editMode)) {
                    setPageState((prevState) => ({...prevState, isLoading: true}));

                    const sortField = checkIfSortFieldExists(columnsString, sortingState);

                    olympeDBReadLambda($, $.get(LAMBDA_NAMES.OLYMPE_DB_READ), selectedTable, filterState, pageState.pageSize, pageState.pageSize * pageState.page, sortField.field, sortField.sort)
                        .then((result) => {
                            setPageState((prevState) => ({...prevState, isLoading: false}));
                            const parsedResult = JSON.parse(result[0]);
                            setPageState((prevState) => ({...prevState, total: Number(result[1] ?? 0)}));
                            const rows = buildIdAndAddToList(primaryKeysColumn, parsedResult);
                            setRows(rows);
                        })
                        .catch((error) => {
                            console.error(`Cannot read data from DB. ${error}`);
                            setPageState((prevState) => ({...prevState, isLoading: false}));
                        });
                }
            }, [columnsString, selectedTable, primaryKeysColumn, JSON.stringify(filterState), pageState.page, pageState.pageSize, sortingState]);

            // Fetch main table data when dependencies change
            useEffect(() => {
                fetchMainData(false);
            }, [fetchMainData]);

            const handleCloseErrorPopup = () => {
                setErrorMessages([]);
                setShowErrorPopup(false);
            };

            const EditToolbar = (props) => {
                const {setRows, setRowModesModel} = props;

                const handleClick = () => {
                    const id = Math.floor(Math.random() * 1000);
                    setRows((oldRows) => [...oldRows, {id, isNew: true}]);
                    setRowModesModel((oldModel) => ({
                        ...oldModel,
                        [id]: {mode: GridRowModes.Edit},
                    }));
                    if (pageState.columns.some((col) => col.column === 'company_code')) {
                        const companyCodeFilter = chipFilters.length > 0 ? [{filterColumn: 'company_code', filterValue: chipFilters}] : [];
                        setFilterState([...companyCodeFilter]);
                    } else {
                        setFilterState([]);
                    }

                    setSortingState((prev) => ({...prev, field: 'idx', sort: 'asc'}));
                };
                const handleUploadRecordsClick = (file) => {
                    setShowUploadRecords(true);
                };

                const handleDownloadRecordsClick = () => {
                    const sortToSend = checkIfSortFieldExists(columnsString, sortingState);
                    downloadRecordsLambda(
                        $,
                        $.get('Export Table To Excel'),
                        columnsString,
                        selectedTable,
                        filterState,
                        sortToSend.field,
                        sortToSend.sort,
                        pageState.total,
                        (result) => {
                            if ( result[0] ) {
                                setErrorMessages([]);
                            } else {
                                setErrorMessages(['Something went wrong while generating the excel. Please try again later']);
                            }
                            setShowErrorPopup(!result[0]);
                        },
                        () => {
                            // if tables failed
                            setErrorMessages(['Something went wrong while generating the excel. Please try again later']);
                            setShowErrorPopup(true);
                        },
                    );
                };

                return !hidden && (
                    <GridToolbarContainer>
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Grid item>
                                <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                                    Add record
                                </Button>
                                <Button
                                    color="primary"
                                    component="label"
                                    startIcon={<FileUploadIcon />}
                                    onClick={handleUploadRecordsClick}
                                >
                                    Upload records
                                </Button>
                                <Button
                                    color="primary"
                                    component="label"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={handleDownloadRecordsClick}
                                >
                                    Download records
                                </Button>
                                <FormControl sx={{minWidth: 120, marginRight: '20px'}} size="small">
                                    <Select
                                        label={'Table'}
                                        value={selectedTable}
                                        onChange={handleChangeSelectedTable}
                                    >
                                        {options.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </GridToolbarContainer>
                );
            };

            const handleRowEditStart = (_params, event) => {
                event.defaultMuiPrevented = true;
            };

            const handleRowEditStop = (params, event) => {
                event.defaultMuiPrevented = true;
            };

            const handleEditClick = (id) => () => {
                setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
            };

            const handleSaveClick = (id) => () => {
                setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
            };

            const handleDeleteClick = (id) => () => {
                const currentRow = rows.find((row) => row.id === id);

                olympeDBDeleteLambda($, $.get(LAMBDA_NAMES.OLYMPE_DB_DELETE), selectedTable, currentRow.idx || currentRow.id)
                    .then(async () => {
                        handleProcessSuccess(`The row was successfully deleted`);
                        await insertChangeLog(selectedTable, currentRow, undefined, false, true);
                        fetchMainData(true);
                    })
                    .catch((error) => {
                        const errorMessage = parseError(error);
                        handleProcessError(errorMessage);
                    });
            };

            const handleCancelClick = (id) => () => {
                setRowModesModel({
                    ...rowModesModel,
                    [id]: {mode: GridRowModes.View, ignoreModifications: true},
                });

                const editedRow = rows.find((row) => row.id === id);
                if (editedRow.isNew) {
                    setRows(rows.filter((row) => row.id !== id));
                }
            };

            const insertChangeLog = async (table, oldRow, newRow, newLine, deletedLine) => {
                const changes = [];
                const t = new Transaction();
                if (deletedLine) {
                    changes.push({
                        column: 'All',
                        old: oldRow.idx,
                        new: '<Deleted row>',
                        idx: oldRow.idx,
                        companyCode: oldRow.company_code,
                    });
                } else {
                    Object.keys(newRow).forEach((key) => {
                        if (oldRow[key] !== newRow[key] && key !== 'id' && key !== 'isNew') {
                            changes.push({
                                column: key,
                                old: newLine ? '<New Entry>' : oldRow[key],
                                new: newRow[key],
                                idx: newRow.idx,
                                companyCode: newRow.company_code,
                            });
                        }
                    });
                }

                const currentUser = UserController.getCurrentUserName('EZPR-USER');
                const currentUserName = localStorage.getItem('userFullName');
                const adminChangesMap = {};

                changes.forEach((change) => {
                    const newChange = t.create(AdminChangeLog);
                    t.update(newChange, AdminChangeLog.dateProp, new Date());
                    t.update(newChange, AdminChangeLog.userNameProp, currentUserName);
                    t.update(newChange, AdminChangeLog.userIDProp, currentUser);
                    t.update(newChange, AdminChangeLog.tableNameProp, table);
                    t.update(newChange, AdminChangeLog.columnProp, change.column);
                    t.update(newChange, AdminChangeLog.oldValueProp, change.old);
                    t.update(newChange, AdminChangeLog.newValueProp, change.new);
                    t.update(newChange, AdminChangeLog.idxProp, change.idx);
                    t.update(newChange, AdminChangeLog.companyCodeProp, change.companyCode);

                    if (!adminChangesMap[change.companyCode]) {
                        adminChangesMap[change.companyCode] = {changes: [], admins: []};
                    }
                    adminChangesMap[change.companyCode].changes.push({
                        idx: change.idx,
                        date: format(new Date(), 'dd.MM.yyyy HH:mm'),
                        userName: currentUserName,
                        userID: currentUser,
                        table: ADMIN_APP_TABLE_NAMES[table],
                        column: ADMIN_APP_TABLE_COLUMNS[change.column],
                        oldValue: change.old,
                        newValue: change.new,
                    });
                });

                adminUserList.forEach((admin) => {
                    if (admin.company_code && admin.company_code !== '') {
                        for (const companyCode in adminChangesMap) {
                            if (Object.prototype.hasOwnProperty.call(adminChangesMap, companyCode) && admin.company_code.includes(companyCode)) {
                                adminChangesMap[companyCode].admins.push(admin.email);
                            }
                        }
                    }
                });

                for (const companyCode in adminChangesMap) {
                    if (Object.prototype.hasOwnProperty.call(adminChangesMap, companyCode)) {
                        const {admins, changes} = adminChangesMap[companyCode];
                        const mailContent = getMailContentAdminAppChange(companyCode, changes);
                        const adminEmails = admins.join(';');
                        if (adminEmails) {
                            sendEmailLambda($, sendEmail, adminEmails, '', '', `[Easy Request] Configuration table change`, mailContent, []);
                        }
                    }
                }

                if (table === 'admin_app_user_list_mapping') {
                    fetchAdminUserListData();
                }

                await t.execute();
            };

            const processRowUpdate = async (newRow, oldRow) => {
                if (selectedTable === 'catalogs') {
                    // If we update the catalogs table, we define the protocol accordingly
                    if (newRow.username && (!newRow.from_id && !newRow.to_id)) {
                        newRow.protocol = 'OCI';
                    } else if (newRow.from_id && newRow.to_id && !newRow.username) {
                        newRow.protocol = 'cXML';
                    } else {
                        newRow.protocol = 'Undefined';
                    }

                    // And if we update the vendor code, we fetch the vendor name accordingly
                    if (newRow.vendor_code) {
                        try {
                            const vendorNameResponse = await getVendorNameLambda(
                                $,
                                $.get('Get Vendor Name'),
                                newRow.vendor_code,
                            );
                            newRow.vendor_name = Array.isArray(vendorNameResponse) && vendorNameResponse[0] !== null ?
                                vendorNameResponse[0] :
                                '';
                        } catch (error) {
                            console.error('Error fetching vendor name:', error);
                            newRow.vendor_name = '';
                        }
                    }
                }
                if (Object.prototype.hasOwnProperty.call(newRow, 'isNew') && newRow.isNew) {
                    const row = {...newRow, isNew: false};

                    if (selectedTable === 'catalogs') {
                        // If we update the password, we encrypt it
                        if (selectedTable === 'catalogs' && row.password) {
                            try {
                                const encryptedPassword = await encryptPasswordLambda(
                                    $,
                                    $.get('Encrypt Password'),
                                    row.password,
                                );
                                row.password = encryptedPassword[0];
                            } catch (error) {
                                console.error('Error encrypting password for new row:', error);
                            }
                        }
                    }

                    const preparedDataForCreation = prepareDataForPostReq(columnsString, row);

                    olympeDBCreateLambda($, $.get(LAMBDA_NAMES.OLYMPE_DB_CREATE), selectedTable, preparedDataForCreation)
                        .then(async () => {
                            handleProcessSuccess(`The row was successfully created`);
                            fetchMainData(true);
                            await insertChangeLog(selectedTable, oldRow, newRow, true, false);
                        })
                        .catch((error) => {
                            const errorMessage = parseError(error);
                            console.error('Error inserting new row:', errorMessage);
                            handleProcessError(errorMessage);
                        });
                } else {
                    const updatedRow = {...newRow, isNew: false};

                    if (selectedTable === 'catalogs') {
                        const passwordField = 'password';
                        if (newRow[passwordField] === oldRow[passwordField] || !newRow[passwordField] || newRow[passwordField].trim() === '') {
                            updatedRow[passwordField] = oldRow[passwordField];
                        } else {
                            try {
                                const encryptedPassword = await encryptPasswordLambda(
                                    $,
                                    $.get('Encrypt Password'),
                                    newRow[passwordField],
                                );
                                updatedRow[passwordField] = encryptedPassword[0];
                            } catch (error) {
                                console.error('Error encrypting password:', error);
                            }
                        }
                    }

                    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
                    const preparedData = prepareDataForPostReq(columnsString, updatedRow);
                    selectedTable === 'company_code_disabling_dyn' || selectedTable === 'nestbuy_company_code' ?
                        preparedData.old_company_code = newRow.id : selectedTable === 'admin_app_user_list_mapping' ?
                            preparedData.old_user_id = newRow.id : preparedData;

                    olympeDBUpdateLambda($, $.get(LAMBDA_NAMES.OLYMPE_DB_UPDATE), selectedTable, preparedData)
                        .then(async () => {
                            handleProcessSuccess(`The row was successfully updated`);
                            await insertChangeLog(selectedTable, oldRow, newRow, false, false);
                        })
                        .catch((error) => {
                            const errorMessage = parseError(error);
                            handleProcessError(errorMessage);
                        });
                    return updatedRow;
                }
            };

            const handleRowModesModelChange = (newRowModesModel) => {
                setRowModesModel(newRowModesModel);
            };

            const handlePageChange = (newPage) => {
                setPageState((old) => ({...old, page: newPage}));
            };

            const handlePageSizeChange = (newPageSize) => {
                setPageState((old) => ({...old, pageSize: newPageSize}));
            };

            const stringOperators = getGridStringOperators().filter(((op) => ['contains'].includes(op.value))); // leave only contains operator

            const uppercaseFields = new Set(['unique_name', 'company_code', 'maximum_1_currency', 'company_codes', 'user_id', 'purchasing_group', 'grp_name', 'commodity', 'total_cost']);

            const columnsMainStructure = useMemo(() => {
                return pageState.columns.map((item) => {
                    if (item.column === 'password' && selectedTable === 'catalogs') {
                        return {
                            field: item.column,
                            headerName: item.columnDisplayName,
                            width: item.width ?? 180,
                            editable: true,
                            sortable: false,
                            renderCell: (params) => (params.value ? '********' : ''),
                            renderEditCell: (params) => <PasswordEditCell {...params} />,
                        };
                    }

                    if (selectedTable === 'catalogs' && item.column === 'vendor_code') {
                        return {
                            field: item.column,
                            headerName: item.columnDisplayName,
                            width: item.width ?? 180,
                            editable: true,
                            sortable: false,
                            renderEditCell: (params) => (
                                <VendorCodeEditCell {...params} getVendorNameLambda={getVendorNameLambda} context={$} />
                            ),
                        };
                    }

                    if (selectedTable === 'catalogs' && item.column === 'vendor_name') {
                        return {
                            field: 'vendor_name',
                            headerName: item.columnDisplayName,
                            width: item.width ?? 180,
                            editable: true,
                            sortable: false,
                            renderEditCell: (params) => (
                                <TextField value={params.value || ''} disabled fullWidth />
                            ),
                            cellClassName: 'greyed-out-cell',
                            editCellClassName: 'greyed-out-cell',
                        };
                    }

                    if (item.column === 'protocol' && selectedTable === 'catalogs') {
                        return {
                            field: item.column,
                            headerName: item.columnDisplayName,
                            width: item.width ?? 180,
                            editable: false,
                            sortable: false,
                            renderCell: (params) => <ProtocolChip protocol={params.value} />,
                            renderEditCell: (params) => <ProtocolChip protocol={params.value} />,
                        };
                    }

                    const isUppercaseField = uppercaseFields.has(item.column);
                    return {
                        field: item.column,
                        headerName: item.columnDisplayName,
                        type: item.type,
                        width: item.width ?? 180,
                        editable: item.column !== 'idx',
                        sortable: false,
                        filterOperators: stringOperators,
                        filterable: item.column !== 'company_code',
                        renderEditCell: (params) => {
                            const handleChange = (event) => {
                                let value = event.target.value;
                                if (isUppercaseField && item.type === 'string') {
                                    value = value.toUpperCase();
                                }

                                params.api.setEditCellValue({id: params.id, field: params.field, value});
                            };

                            if (item.type === 'string') {
                                return (
                                    <TextField
                                        value={params.value}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                );
                            } else if (item.type === 'number') {
                                return (
                                    <TextField
                                        type="number"
                                        value={params.value}
                                        onChange={handleChange}
                                        fullWidth
                                        InputProps={{inputProps: {min: 0}}}
                                    />
                                );
                            }
                        },
                    };
                });
            }, [pageState.columns, stringOperators, uppercaseFields]);


            const columns = [
                ...columnsMainStructure,
                {
                    field: 'actions',
                    type: 'actions',
                    headerName: 'Actions',
                    width: 100,
                    cellClassName: 'actions',
                    getActions: ({id}) => {
                        if (rowsDisabled) {
                            return [];
                        }

                        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                        if (isInEditMode) {
                            return [
                                <GridActionsCellItem
                                    icon={<SaveIcon />}
                                    label="Save"
                                    key={1}
                                    onClick={handleSaveClick(id)}
                                />,
                                <GridActionsCellItem
                                    icon={<CancelIcon />}
                                    label="Cancel"
                                    className="textPrimary"
                                    key={2}
                                    onClick={handleCancelClick(id)}
                                    color="inherit"
                                />,
                            ];
                        }
                        return [
                            <GridActionsCellItem
                                icon={<EditIcon />}
                                label="Edit"
                                className="textPrimary"
                                onClick={handleEditClick(id)}
                                color="inherit"
                                key={3}
                            />,
                            <GridActionsCellItem
                                icon={<DeleteIcon />}
                                label="Delete"
                                key={4}
                                onClick={handleDeleteClick(id)}
                                color="inherit"
                            />,
                        ];
                    },
                },
            ];

            const onFilterChange = (filterModel) => {
                const newFilters = filterModel.items
                    .filter((filter) => filter.columnField !== 'company_code') // Ignore internal filters on company_code
                    .map((filter) => ({filterColumn: filter.columnField, filterValue: filter.value}));

                const companyCodeFilter = pageState.columns.some((col) => col.column === 'company_code') && chipFilters.length > 0 ?
                    [{filterColumn: 'company_code', filterValue: chipFilters}] :
                    [];

                setFilterState([...newFilters, ...companyCodeFilter]);
            };

            const onSortChange = (newSortModel) => {
                setSortingState((prev) => ({...prev, field: newSortModel[0]?.field ?? '', sort: newSortModel[0]?.sort}));
            };

            const handleUploadRecordsClose = () => {
                setShowUploadRecords(false);
            };

            const sxProp = rowsDisabled ? {
                '.MuiDataGrid-row': {
                    backgroundColor: '#f5f5f5',
                    pointerEvents: 'none',
                    opacity: 0.6,
                },
            } : undefined;

            return (
                <div style={{height: '100%', width: '100%'}}>
                    <Tooltip title={`${rowsDisabled ? 'Please select a company code to enable editing' : ''}`} placement={'top'}>
                        <div style={{height: '100%', width: '100%'}}>
                            <DataGridPro
                                rows={rows}
                                columns={columns}
                                editMode="row"
                                loading={pageState.isLoading}
                                rowModesModel={rowModesModel}
                                experimentalFeatures={{newEditingApi: true}}
                                onRowModesModelChange={handleRowModesModelChange}
                                onRowEditStart={handleRowEditStart}
                                onRowEditStop={handleRowEditStop}
                                processRowUpdate={processRowUpdate}
                                onPageSizeChange={handlePageSizeChange}
                                rowsPerPageOptions={[5, 10, 25]}
                                pagination
                                paginationMode="server"
                                page={pageState.page}
                                pageSize={pageState.pageSize}
                                onPageChange={handlePageChange}
                                rowCount={pageState.total}
                                filterMode="server"
                                onFilterModelChange={onFilterChange}
                                onSortModelChange={onSortChange}
                                onProcessRowUpdateError={(err) => console.log('****', err)}
                                components={{
                                    Toolbar: EditToolbar,
                                }}
                                componentsProps={{
                                    toolbar: {setRows, setRowModesModel},
                                    filterPanel: {linkOperators: [GridLinkOperator.And]},
                                    pagination: {
                                        showFirstButton: true,
                                        showLastButton: true,
                                    },
                                }}
                                sx={{
                                    ...sxProp,
                                    '& .greyed-out-cell': {
                                        backgroundColor: '#fafafa',
                                    },
                                    '& .MuiDataGrid-row--editing .MuiDataGrid-cell.greyed-out-cell': {
                                        backgroundColor: '#fafafa !important',
                                    },
                                }}
                            />
                        </div>
                    </Tooltip>
                    {!!snackbar && (
                        <Snackbar
                            open
                            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                            onClose={handleCloseSnackbar}
                            autoHideDuration={6000}
                        >
                            <Alert {...snackbar} onClose={handleCloseSnackbar} />
                        </Snackbar>
                    )}
                    {showUploadRecords && (
                        <UploadRecordsPopup
                            selectedTable={selectedTableName.current}
                            selectedTableValue={selectedTable}
                            columns={columnsString}
                            columnsDisplayName={columnsDisplayName}
                            handleOnClose={handleUploadRecordsClose}
                            context={$}
                            adminUserList={adminUserList}
                            sendEmail={sendEmail}
                        />
                    )}
                    <ErrorPopup
                        onClose={handleCloseErrorPopup}
                        open={showErrorPopup}
                        errorMessages={errorMessages}
                    />
                </div>
            );
        };
    }
}

registerBrick('0188aee0223da5d9e656', Datagrid);
